import { type FC, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";

import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";

import {
  VehicleForm,
  EMPTY_VEHICLE_FORM,
  buildVehicleName,
  validateVehicleFields,
  vehicleToItemFields,
  type VehicleFormValues,
  type VehicleFieldErrors,
} from "./VehicleForm";

export const AddVehicle: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    VehicleFormValues,
    VehicleFieldErrors
  >(EMPTY_VEHICLE_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting || isAddingItem) return;

    const errors = validateVehicleFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    if (!estateId) return;

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;

      if (form.titleFile) {
        sourceDocumentId = await uploadDocumentWithAuth(form.titleFile, estateId, "vehicle_title");
      }

      const fields = vehicleToItemFields(form);
      const vehicleName = buildVehicleName(form);

      const metadata: Record<string, unknown> = { ...fields.metadata };

      const created = await addItem({
        estate_id: estateId,
        category: "Vehicles",
        classification: "asset",
        item_name: vehicleName,
        item_type: fields.item_type,
        balance: fields.balance,
        metadata,
        source_document_id: sourceDocumentId,
      });

      const updatedAnswers = {
        ...answers,
        vehicles_review: "has_items",
        add_vehicle: "done",
        has_auto_loan: form.hasLoan || "",
        last_vehicle_id: created?.id ? String(created.id) : "",
        vehicle_estimate_done: "",
        auto_loan_done: "",
      };

      if (form.hasLoan === "yes") {
        goToScreen("add_auto_loan", updatedAnswers);
      } else {
        goToScreen("vehicle_estimate", updatedAnswers);
      }
    } catch (err) {
      setError("Failed to add item. Please try again.");
      sentryUtils.captureError(err, "AddVehicle submission failed", {
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AddItemLayout
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      error={error}
      disabled={isAddingItem}
    >
      <VehicleForm form={form} onChange={updateForm} fieldErrors={fieldErrors} showValue={false} />
    </AddItemLayout>
  );
};
